import { ref, reactive } from 'vue';
  import { v4 as uuidv4 } from 'uuid';
  import {
    getAdmins,
    createAdmin,
    updateAdmin,
    changeAdminStatus
  } from '../services/admins.js';
  import { toast } from 'vue3-toastify';
  import { isPermissionError } from '@/common/static_data/constants';

  export function useAdmins() {
    const admins = ref([]);
    const isLoading = reactive({
      fetch: false,
      create: false,
      update: false,
      delete: false
    });
    const errorMessage = ref('');
    const currentAdmin = ref(null);
    
    const pagination = reactive({
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0
    });
    const fetchAdmins = async (page = 1) => {
      isLoading.fetch = true;
      errorMessage.value = '';

      try {
        const response = await getAdmins({
          page,
          limit: pagination.itemsPerPage
        });

        admins.value = response.data;
        pagination.totalItems = response.total;
        pagination.currentPage = page;
      } catch (error) {
        if (isPermissionError(error)) {
          admins.value = [];
          pagination.totalItems = 0;
        } else {
          errorMessage.value = 'Error loading administrators';
          toast.error('Failed to load administrators');
        }
      } finally {
        isLoading.fetch = false;
      }
    };
    const addAdmin = async (adminData) => {
      isLoading.create = true;
      errorMessage.value = '';

      try {
        const newAdmin = {
          id: uuidv4(),
          ...adminData,
          status: 'active',
          createdAt: new Date().toISOString()
        };

        await createAdmin(newAdmin);
        await fetchAdmins(pagination.currentPage);

        toast.success('Administrator created successfully! Password reset email has been sent.');
        return { success: true };
      } catch (error) {
        errorMessage.value = error.message || 'Error creating administrator';
        toast.error('Failed to create administrator');
        console.error('Error creating admin:', error);
        return { success: false, error: error.message };
      } finally {
        isLoading.create = false;
      }
    };

    const editAdmin = async (adminData) => {
      isLoading.update = true;
      errorMessage.value = '';

      try {
        await updateAdmin(adminData.id, adminData);
        await fetchAdmins(pagination.currentPage);

        toast.success('Administrator updated successfully');
        return { success: true };
      } catch (error) {
        errorMessage.value = error.message || 'Error updating administrator';
        toast.error('Failed to update administrator');
        console.error('Error updating admin:', error);
        return { success: false, error: error.message };
      } finally {
        isLoading.update = false;
      }
    };
    const toggleAdminStatus = async (admin) => {
      isLoading.delete = true;
      errorMessage.value = '';

      try {
        const newStatus = !admin.isActive;
        await changeAdminStatus(admin.id, newStatus);
        await fetchAdmins(pagination.currentPage);

        const action = newStatus ? 'activated' : 'deactivated';
        toast.success(`Administrator ${action} successfully`);
        return { success: true };
      } catch (error) {
        errorMessage.value = error.message || 'Error changing administrator status';
        toast.error('Failed to change administrator status');
        console.error('Error changing admin status:', error);
        return { success: false, error: error.message };
      } finally {
        isLoading.delete = false;
      }
    };
    const setCurrentAdmin = (admin) => {
      currentAdmin.value = admin;
    };

    const clearCurrentAdmin = () => {
      currentAdmin.value = null;
      errorMessage.value = '';
    };

    const handlePagination = (params) => {
      if (params.limit) {
        pagination.itemsPerPage = params.limit;
      }
      if (params.currentPage) {
        fetchAdmins(params.currentPage);
      } else {
        fetchAdmins(pagination.currentPage);
      }
    };

    return {
      admins,
      isLoading,
      errorMessage,
      currentAdmin,
      pagination,

      fetchAdmins,
      addAdmin,
      editAdmin,
      toggleAdminStatus,
      
      setCurrentAdmin,
      clearCurrentAdmin,
      handlePagination
    };
  }